import { useState } from "react";
import { Link, Redirect, Route, Switch, useLocation } from "wouter";
import { trpc } from "../../_core/trpc";
import { useAuth } from "../../_core/useAuth";
import { publicUrl } from "../../_core/public-url";
import AdminContent from "./AdminContent";
import AdminPhotos from "./AdminPhotos";
import AdminVideos from "./AdminVideos";
import AdminMusic from "./AdminMusic";
import AdminSchedule from "./AdminSchedule";
import AdminSettings from "./AdminSettings";
import AdminOverview from "./AdminOverview";
const SECTIONS = [
  { path: "/admin", label: "Overview", icon: "M3 12h7V3H3zM14 21h7V10h-7zM3 21h7v-6H3zM14 7h7V3h-7z" },
  { path: "/admin/content", label: "Words", icon: "M4 5h16M4 12h16M4 19h10" },
  { path: "/admin/photos", label: "Photos", icon: "M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6" },
  { path: "/admin/videos", label: "Video", icon: "M3 6h12v12H3zM15 10l6-3v10l-6-3z" },
  { path: "/admin/music", label: "Music", icon: "M9 18V6l10-2v12M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" },
  { path: "/admin/schedule", label: "Midnight", icon: "M12 3a9 9 0 1 0 9 9M12 7v5l3 2M20 3v5h-5" },
  { path: "/admin/settings", label: "Settings", icon: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM4 12h2M18 12h2M12 4v2M12 18v2" }
];
function Admin() {
  const { user, isLoading, logout } = useAuth();
  const [location] = useLocation();
  const status = trpc.auth.adminStatus.useQuery();
  const [copied, setCopied] = useState(false);
  if (isLoading || status.isLoading) {
    return <div className="admin-shell grid min-h-[100svh] place-items-center">
        <p className="eyebrow">Opening the studio…</p>
      </div>;
  }
  if (!user) return <Redirect to="/admin/login" />;
  if (!status.data?.isAdmin) return <Redirect to="/admin/login" />;
  const siteLink = publicUrl("/");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(siteLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };
  return <div className="admin-shell min-h-[100svh] text-[#f3f4f9]">
      {
    /* ── top bar ── */
  }
      <header className="sticky top-0 z-30 border-b border-[rgba(233,168,242,.16)] bg-[rgba(11,12,22,.86)] backdrop-blur-xl">
        <div
    className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6"
    style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
  >
          <Link to="/admin" className="min-w-0 truncate font-display text-base tracking-wide gold-text sm:text-lg">
            {
    /* The wordmark used to be non-breaking and clipped to "Midnig…" on a
       phone. It is allowed to shrink now, and the tagline pill is hidden
       below `sm` to give it the room. */
  }
            <span className="hidden sm:inline">Midnight&nbsp;for&nbsp;Baby</span>
            <span className="sm:hidden">Midnight</span>
          </Link>
          <span className="pill hidden shrink-0 sm:inline-flex">Owner studio</span>
          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            <a
    href={siteLink}
    target="_blank"
    rel="noreferrer"
    className="btn-ghost-lux !min-h-[40px] !px-3 !text-[0.62rem] sm:!px-4 sm:!text-[0.66rem]"
  >
              View site
            </a>
            <button
    type="button"
    data-testid="admin-signout-btn"
    onClick={() => void logout()}
    className="rounded-full px-2.5 py-2.5 text-[0.62rem] uppercase tracking-[0.14em] text-[color:var(--muted-foreground)] transition hover:text-[#e9a8f2] sm:px-3"
  >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 pb-24 pt-6 sm:px-6" style={{ paddingBottom: "calc(5rem + env(safe-area-inset-bottom, 0px))" }}>
        {
    /* ── share link ── */
  }
        <div className="glass mb-6 rounded-xl p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <p className="eyebrow">Her link</p>
              <input
    readOnly
    value={siteLink}
    onFocus={(e) => e.currentTarget.select()}
    aria-label="Public site URL"
    className="field-lux mt-2 !min-h-[40px] truncate !text-[0.8rem]"
  />
            </div>
            <button data-testid="copy-link-btn" type="button" onClick={copy} className="btn-gold !min-h-[40px] shrink-0 !text-[0.66rem]">
              {copied ? "Copied \u2713" : "Copy link"}
            </button>
          </div>
        </div>

        {
    /* ── nav ── */
  }
        {
    /* `overflow-x:auto` here is deliberate: seven sections do not fit across
       a 360px phone, and a wrapping two-row nav pushes the content below
       the fold. `scroll-snap` makes the swipe settle on a section, and the
       scrollbar is hidden because it is a control, not content. */
  }
        <nav
    aria-label="Admin sections"
    className="mb-7 -mx-1 flex snap-x gap-1 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
  >
          {SECTIONS.map((s) => {
    const active = location === s.path;
    return <Link
      key={s.path}
      data-testid={`admin-nav-${s.label.toLowerCase()}`}
      to={s.path}
      aria-current={active ? "page" : void 0}
      className={`flex min-h-[44px] shrink-0 snap-start items-center gap-2 rounded-full px-3.5 py-2 text-[0.72rem] uppercase tracking-[0.14em] transition ${active ? "bg-[rgba(233,168,242,.16)] text-[#e9a8f2] ring-1 ring-[rgba(233,168,242,.4)]" : "text-[color:var(--muted-foreground)] hover:bg-white/5 hover:text-[#dcdff0]"}`}
    >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
      d={s.icon}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
                </svg>
                {s.label}
              </Link>;
  })}
        </nav>

        <Switch>
          <Route path="/admin" component={AdminOverview} />
          <Route path="/admin/content" component={AdminContent} />
          <Route path="/admin/photos" component={AdminPhotos} />
          <Route path="/admin/videos" component={AdminVideos} />
          <Route path="/admin/music" component={AdminMusic} />
          <Route path="/admin/schedule" component={AdminSchedule} />
          <Route path="/admin/settings" component={AdminSettings} />
        </Switch>
      </div>
    </div>;
}
function Panel({
  eyebrow,
  title,
  description,
  children,
  aside
}) {
  return <section>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="display-md mt-2 text-[#f3f4f9]">{title}</h2>
          {description && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[color:var(--muted-foreground)]">
              {description}
            </p>}
        </div>
        {aside}
      </div>
      {children}
    </section>;
}
export {
  Panel,
  Admin as default
};
